import useProcess from '@hooks/useProcess';
import type { MenuItem } from '@pages/Settings/menu';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ParamsList } from '@/routes/routes.types';

import {
  SubheaderContainer,
  SubheaderMenu,
  TabsHighlight
} from './sub-header.style';

type TabName = MenuItem['name'];
type SubHeaderContextProps = {
  repositionHighlight: (e: React.MouseEvent, tab: boolean) => void;
  activeNavItem: string | null;
  handleNavItemClick: (name: string) => void;
};
const SubHeaderContext = createContext<SubHeaderContextProps | null>(null);

export const useSubHeaderContext = () => {
  const context = useContext(SubHeaderContext);
  if (!context) {
    throw new Error('useSubHeaderContext must be used within SubHeader');
  }
  return context;
};

export const SubHeader = ({ children }) => {
  const process = useProcess();
  const _location = useLocation();
  const processId = useParams()[ParamsList.ProcessId] as string;
  const currentWorkflowId = useParams()[ParamsList.WorkflowId];

  const [tabBoundingBox, setTabBoundingBox] = React.useState<Maybe<DOMRect>>();
  const [wrapperBoundingBox, setWrapperBoundingBox] =
    React.useState<Maybe<DOMRect>>();
  const [highlightedTab, setHighlightedTab] = React.useState<boolean | null>(
    null
  );
  const [isHoveredFromNull, setIsHoveredFromNull] = React.useState(true);
  const [activeNavItem, setActiveNavItem] = useState<string | null>(null);

  const handleNavItemClick = (navName: string) => {
    setActiveNavItem(navName);
  };

  const highlightRef = React.useRef(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // biome-ignore lint/suspicious/noExplicitAny: ok.ici
  const repositionHighlight = (e: any, tab: boolean) => {
    setTabBoundingBox(
      // eslint-disable-next-line
      e.target?.getBoundingClientRect() as unknown as Maybe<DOMRect>
    );
    setWrapperBoundingBox(wrapperRef.current?.getBoundingClientRect());
    setIsHoveredFromNull(!highlightedTab);
    setHighlightedTab(tab);
  };

  const resetHighlight = () => {
    setHighlightedTab(null);
  };

  const highlightStyles: React.CSSProperties = {};

  if (tabBoundingBox && wrapperBoundingBox) {
    highlightStyles.transitionDuration = isHoveredFromNull ? '0ms' : '150ms';
    highlightStyles.width = `${tabBoundingBox.width}px`;
    highlightStyles.opacity = highlightedTab ? 1 : 0;
    highlightStyles.transform = `translate(${
      tabBoundingBox.left - wrapperBoundingBox.left
    }px)`;
  }
  useEffect(() => {
    let tabName: TabName;
    if (currentWorkflowId) {
      tabName = window.location.pathname
        .split(`${currentWorkflowId}/`)[1]
        ?.split('/')[0];
    } else {
      tabName = window.location.pathname
        .split(`${processId}/`)[1]
        ?.split('/')[0];
    }

    setActiveNavItem(tabName);
  }, [currentWorkflowId, processId]);

  if (!process) {
    return null;
  }

  return (
    <SubHeaderContext.Provider
      value={{ repositionHighlight, activeNavItem, handleNavItemClick }}
    >
      <SubheaderContainer
        className="relative flex h-10 w-full flex-row gap-x-1 border-gray-200 border-b border-solid bg-white pl-[23px]"
        onMouseLeave={resetHighlight}
        ref={wrapperRef}
      >
        <TabsHighlight
          className="bg-gray-200"
          ref={highlightRef}
          style={highlightStyles}
        />
        <SubheaderMenu>{children}</SubheaderMenu>
      </SubheaderContainer>
    </SubHeaderContext.Provider>
  );
};
